/**
 * caseReportDoc.js — display list → jsPDF.
 *
 * A thin, dumb writer. All the layout decisions were made in layout.js; this
 * file only knows how to put an op on a page. Keeping it dumb is what makes the
 * on-screen preview trustworthy: the preview runs the same list through a canvas
 * renderer, so "it looked right but exported wrong" is not a failure mode that
 * exists here.
 *
 * The caller supplies the pixels. `resolveImage(source)` is handed the very
 * `{kind:'scene'|'asset', …}` descriptor layout emitted and returns a data URL —
 * so this module needs to know nothing about meshes, Drive or the render cache.
 */

import {
  PW, PH, registerMulish, asDataUrl, toJpeg, cropToContent, contentBox,
  watermarkLattice, overWhite, stampWatermark,
} from '../pdfChassis.js'
import { layoutReport, unsupportedAnnotations } from './layout.js'
import { geometryFor } from './formats.js'
import { newReport, newTextPage, looseSheetMeta } from './schema.js'

/**
 * @param {object} doc                      a validated ReportDoc
 * @param {object} opts
 * @param {(source:object) => Promise<string|null>} opts.resolveImage
 *        data URL for a scene view or an asset; null renders an empty box.
 * @param {string} [opts.logo]              logo data URL (carries the tagline)
 * @param {number} [opts.logoAspect=3]
 * @param {(done:number,total:number)=>void} [opts.onProgress]
 * @returns {Promise<{doc: import('jspdf').jsPDF, warnings: string[]}>}
 */
/**
 * A layout op's `weight` as a jsPDF font style. Only the embedded Mulish has a `medium`
 * face (the master's footer small print); the helvetica fallback maps it to normal, so a
 * missing font file changes the weight and not the page.
 */
const fontStyle = (font, weight, faces = null) => (
  weight === 'bold' ? 'bold'
    : weight === 'medium' && font === 'Mulish' && (!faces || faces.includes('medium')) ? 'medium'
      : 'normal')

export async function buildCaseReport(doc, {
  resolveImage, logo = null, logoAspect = 3, onProgress = null, geometry = null,
} = {}) {
  if (typeof resolveImage !== 'function') {
    throw new TypeError('buildCaseReport: resolveImage is required')
  }
  const { jsPDF } = await import('jspdf')
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const font = await registerMulish(pdf, { medium: true })
  const faces = pdf.getFontList?.()?.[font] ?? null
  const g = geometry ?? geometryFor(doc.template)
  if (!g) throw new Error(`buildCaseReport: no geometry for the "${doc.template}" format`)
  // The house template's mark — WHICH template made this file — goes into the
  // PDF's document properties and nowhere on the page. Deepak, 26 Sep: "Version
  // information is NOT required anywhere. It is going to be generated by
  // portal, so you can keep something hidden and small just for you to read."
  // Only a geometry that carries a mark stamps one, so the measured templates'
  // goldens do not move.
  if (g.templateMark) {
    pdf.setProperties({ creator: 'Osteo3d Case Report Builder', keywords: g.templateMark })
  }

  const warnings = []
  // One warning per document per cause. Repeating one page-independent fault
  // picture by picture is 30 lines on a 14-page report, and a warning on every
  // page teaches the operator to ignore warnings.
  const said = new Set()
  const unsupported = unsupportedAnnotations(doc)
  if (unsupported.length) {
    // Loud, not silent: the operator placed these and would otherwise assume
    // they exported.
    warnings.push(
      `${unsupported.join(', ')} annotation(s) are in the report but this build cannot draw them ` +
      '— they are missing from the PDF.',
    )
  }

  const measureText = (text, size, weight) => {
    pdf.setFont(font, fontStyle(font, weight, faces))
    pdf.setFontSize(size)
    return pdf.getTextWidth(String(text ?? ''))
  }

  const pages = layoutReport(doc, { measureText, geometry: g })

  for (const [i, { ops }] of pages.entries()) {
    if (i > 0) pdf.addPage()
    await cropTightGroups(ops, resolveImage)
    for (const op of ops) {
      await drawOp(pdf, op, {
        font, faces, logo, logoAspect, resolveImage, warnings, said,
        // The SAME fallback the watermark op takes, or the page would draw tiles
        // that no picture stamps and every opaque JPEG would erase them — the
        // field defect the old paint order existed to fix.
        watermark: g.watermark ?? HOUSE_WATERMARK,
      })
    }
    onProgress?.(i + 1, pages.length)
  }

  return { doc: pdf, warnings }
}

/**
 * A case-report render sits on pure white and lights bone up to ~20 below white, so a pixel
 * counts as ink at a smaller distance than the SSE report's default of 30 — otherwise the
 * brightest bone (the dome top, the occiput) is trimmed off the crop box.
 */
const RENDER_INK_MIN_DIFF = 12

/**
 * Images sharing a `tightGroup` (the comparison page's six cells) are cropped to ONE
 * rectangle: the union of their ink boxes. Each keeps its own pixels and its own contain-fit,
 * so the shared scale the renderer gave them survives while the empty margin goes. Only
 * renders (`kind: 'scene'`) join the union — an uploaded picture in a cell has its own size and
 * would pull the rectangle off the renders. The resolved source is kept on the op so the draw
 * does not fetch it again. A group whose images cannot be read is left as it is.
 */
export async function cropTightGroups(ops, resolveImage) {
  const groups = new Map()
  for (const op of ops) {
    if (op.op === 'image' && op.tightGroup != null && !op.crop && op.source?.kind === 'scene') {
      if (!groups.has(op.tightGroup)) groups.set(op.tightGroup, [])
      groups.get(op.tightGroup).push(op)
    }
  }
  for (const members of groups.values()) {
    let union = null
    for (const op of members) {
      const source = await resolveImage(op.source)
      if (!source) continue
      op.resolved = source
      let box = null
      try { box = await contentBox(asDataUrl(source), { minDiff: RENDER_INK_MIN_DIFF }) } catch { box = null }
      if (!box) continue
      union = union ? {
        x: Math.min(union.x, box.x), y: Math.min(union.y, box.y),
        x1: Math.max(union.x1, box.x + box.w), y1: Math.max(union.y1, box.y + box.h),
      } : { x: box.x, y: box.y, x1: box.x + box.w, y1: box.y + box.h }
    }
    if (!union) continue
    const crop = { x: union.x, y: union.y, w: union.x1 - union.x, h: union.y1 - union.y }
    for (const op of members) op.crop = crop
  }
}

async function drawOp(pdf, op, ctx) {
  switch (op.op) {
    case 'watermark':
      // A geometry with no watermark spec gets the shared one, drawn the same
      // way. It must NOT fall through to pdfChassis.tiledWatermark: that one
      // still sets an alpha graphics state, which is the whole defect, and it
      // belongs to the SSE report whose bytes are not moving.
      drawWatermark(pdf, ctx.font, op.text ? op : HOUSE_WATERMARK)
      break

    case 'logo': {
      if (!ctx.logo) break
      const h = op.h
      const w = op.w ?? h * (ctx.logoAspect || 3)
      // layout gives the logo's horizontal CENTRE, since the aspect (and so the
      // width) is only known here.
      // 🔴 The asset must stay a PNG with NO alpha channel (colour type 2).
      // jsPDF attaches a soft mask to any colour-type-6 PNG even when every
      // pixel is opaque, and that ONE soft mask, 16 mm tall in the corner, was
      // enough on its own to rasterise the whole sheet in a print path that
      // cannot composite: measured A/B on the same page, 0 vector path points
      // with it and 12 without. Small and local is NOT safe. Re-exporting the
      // logo the usual way, with transparency, brings the print defect back —
      // template.test.js reads the shipped file's colour type for that reason.
      try { pdf.addImage(ctx.logo, 'PNG', op.x - w / 2, op.y, w, h) } catch { /* ignore */ }
      break
    }

    case 'line':
      pdf.setDrawColor(...(op.color ?? [0, 0, 0]))
      pdf.setLineWidth(op.width ?? 0.2)
      pdf.line(op.x1, op.y1, op.x2, op.y2)
      break

    case 'rect':
      // Colours arrive as [r,g,b]; a hex string is accepted too because the
      // legend's swatch colours come from palette.js, where the team's values
      // are recorded as hex. Spreading a string into setFillColor would feed it
      // one character per argument and paint the wrong colour silently.
      if (op.fill) { pdf.setFillColor(...asRgb(op.fill)) }
      if (op.stroke) { pdf.setDrawColor(...asRgb(op.stroke)); pdf.setLineWidth(op.lineWidth ?? 0.2) }
      pdf.rect(op.x, op.y, op.w, op.h, op.fill && op.stroke ? 'FD' : op.fill ? 'F' : 'S')
      break

    case 'ellipse':
      pdf.setDrawColor(...(op.stroke ?? [0, 0, 0]))
      pdf.setLineWidth(op.lineWidth ?? 0.4)
      pdf.ellipse(op.cx, op.cy, op.rx, op.ry, 'S')
      break

    case 'text': {
      pdf.setFont(ctx.font, fontStyle(ctx.font, op.weight, ctx.faces))
      pdf.setFontSize(op.size)
      const text = sanitizeForPdf(op.text)
      if (op.halo) {
        // jsPDF has no text shadow. A white ring at ±0.2mm keeps an annotation
        // legible over a mid-tone render, which is the whole reason to place one.
        pdf.setTextColor(255, 255, 255)
        for (const [dx, dy] of [[-0.2, 0], [0.2, 0], [0, -0.2], [0, 0.2]]) {
          pdf.text(text, op.x + dx, op.y + dy, { align: op.align, angle: op.angle })
        }
      }
      pdf.setTextColor(...(op.color ?? [0, 0, 0]))
      // `angle` is what makes a dimension label readable on a lateral view,
      // where the reference sets the value upright beside a vertical extension
      // line (p13). jsPDF rotates about the anchor, counter-clockwise.
      pdf.text(text, op.x, op.y, { align: op.align, maxWidth: op.maxWidth, angle: op.angle })
      break
    }

    case 'image': {
      const source = op.resolved ?? await ctx.resolveImage(op.source)
      if (!source) {
        ctx.warnings.push(`missing image for ${describeSource(op.source)}`)
        break
      }
      // A `crop` rectangle (fractions of the render) is applied BEFORE the
      // JPEG conversion and before the contain-fit, so the cropped region's own
      // aspect ratio decides the fit. The orbital report's page 4 uses this to
      // zoom its lateral views to the orbit — ADR 0009 chose a 2D crop over a
      // second camera so the shared renderer cannot be perturbed.
      let src = op.crop
        ? await cropRegion(asDataUrl(source), op.crop)
        : asDataUrl(source)
      // `tight`: crop to the model's own ink so the fit is decided by the model, not by the
      // render's framing (pdfChassis.cropToContent — the same crop the SSE report uses). A
      // source that cannot be read back as pixels is drawn as it is.
      if (op.tight) {
        try { src = (await cropToContent(src, { minDiff: RENDER_INK_MIN_DIFF })).url } catch { /* keep the uncropped render */ }
      }
      // The picture carries its own watermark now. It used to get one from a
      // transparent object drawn over the page, which cost the frame its vector
      // lines in print — pdfChassis.overWhite carries the measurement. The stamp
      // decodes once and hands back the JPEG to embed, plus the contain-fit
      // rectangle it placed the tiles from, so the page and the picture cannot
      // disagree about where that picture is.
      const stamp = await stampWatermark(src, op, ctx.watermark, { font: ctx.font })
      if (stamp.reason && !ctx.said?.has(stamp.reason)) {
        ctx.said?.add(stamp.reason)
        ctx.warnings.push(`the pictures carry no watermark — ${stamp.reason}`)
      }
      const { x, y, w: fw, h: fh } = stamp.fit
      const url = stamp.stamped ? stamp.url : (await toJpeg(stamp.url)).url
      try {
        pdf.addImage(url, 'JPEG', x, y, fw, fh)
      } catch {
        ctx.warnings.push(`could not embed image for ${describeSource(op.source)}`)
      }
      break
    }

    default:
      throw new Error(`caseReportDoc: unknown op "${op.op}"`)
  }
}

/**
 * The case report's tiled watermark, drawn SOLID.
 *
 * Its own function, not pdfChassis.tiledWatermark: that one sets an alpha
 * graphics state, which is the defect (pdfChassis.overWhite), and it belongs to
 * the SSE report whose bytes are not moving. A geometry that states no watermark
 * is routed here too, through HOUSE_WATERMARK below.
 *
 * The master's lattice is about twice as dense on both axes as the SSE report's,
 * measured, which is why its parameters travel with the template.
 */
function drawWatermark(pdf, font, w) {
  // NO alpha: the tile is drawn in the colour it used to render AS, over white
  // (pdfChassis.overWhite carries the measurement this comes from). Every
  // picture stamps its own copy, so white is the only backdrop these tiles meet.
  // The lattice itself is shared with the stamp.
  //
  // The save/restore stays. It is not transparency — it is what puts the page's
  // font key, size and text colour back afterwards. Without it this op leaves
  // 19.97 pt and rgb(236,237,238) behind for everything drawn after it, and it
  // is now the FIRST op on the page.
  pdf.saveGraphicsState()
  pdf.setTextColor(...overWhite(w.color ?? [140, 150, 155], w.opacity ?? 0.09))
  pdf.setFont(font, 'normal')
  pdf.setFontSize(w.size ?? 13)
  for (const { x, y } of watermarkLattice(w)) {
    pdf.text(w.text, x, y, { angle: w.angle ?? 30 })
  }
  pdf.restoreGraphicsState()
}

/**
 * The watermark a geometry that states none falls back to: pdfChassis's own
 * pitch, colour and opacity as data, so the case report never reaches the
 * alpha-setting helper.
 *
 * NOT byte-identical to what pdfChassis.tiledWatermark draws. That one stops at
 * `y < PH` and this file's lattice runs to `y < PH + stepY`, so the fallback
 * lays 60 tiles where the SSE report lays 55. It is reachable only by a geometry
 * with no watermark spec, and `geometryFor()` returns the master for every
 * format the product ships, so nothing delivered takes this path.
 */
const HOUSE_WATERMARK = {
  text: 'Osteo3d ®', size: 13, opacity: 0.09, angle: 30,
  stepX: 55, stepY: 26, color: [140, 150, 155], originX: -10, originY: 20,
}

/**
 * [r,g,b] as given, or a #rrggbb string converted.
 *
 * Belongs here and not in layout because layout is the pure module and states
 * colours the way its authors do; the writer is where they meet jsPDF's API.
 */
function asRgb(c) {
  if (Array.isArray(c)) return c
  const m = /^#?([0-9a-f]{6})$/i.exec(String(c ?? ''))
  if (!m) return [0, 0, 0]
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/**
 * Crop a data-URL image to a fractional rectangle {x,y,w,h} of itself.
 *
 * Fractions rather than pixels, so a recipe's crop survives a change of render
 * resolution — which is the whole reason the render cache can be re-keyed
 * without re-authoring every page. Clamped, so a bad rectangle yields a smaller
 * picture rather than a canvas error mid-document.
 */
async function cropRegion(dataUrl, crop) {
  const im = await new Promise((resolve) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.onerror = () => resolve(null)
    i.src = dataUrl
  })
  if (!im) return dataUrl
  const W = im.naturalWidth, H = im.naturalHeight
  const fx = Math.min(Math.max(crop.x ?? 0, 0), 1)
  const fy = Math.min(Math.max(crop.y ?? 0, 0), 1)
  const fw = Math.min(Math.max(crop.w ?? 1, 0.01), 1 - fx)
  const fh = Math.min(Math.max(crop.h ?? 1, 0.01), 1 - fy)
  const sx = Math.round(fx * W), sy = Math.round(fy * H)
  const sw = Math.max(1, Math.round(fw * W)), sh = Math.max(1, Math.round(fh * H))
  const c = document.createElement('canvas')
  c.width = sw
  c.height = sh
  c.getContext('2d').drawImage(im, sx, sy, sw, sh, 0, 0, sw, sh)
  return c.toDataURL('image/png')
}

const describeSource = (s) =>
  s?.kind === 'scene' ? `scene ${s.sceneId} view ${s.view}` : `asset ${s?.assetId}`

/**
 * jsPDF's Helvetica fallback is WinAnsi-encoded, so a handful of glyphs render
 * as garbage if Mulish failed to embed. Mulish itself has them all; this is
 * insurance for the fallback path only.
 */
const sanitizeForPdf = (s) =>
  String(s ?? '').replace(/→/g, '->').replace(/←/g, '<-').replace(/[’]/g, "'")

/**
 * The document and geometry a blank sheet is built from. Exported so the
 * suppression below can be asserted on the REAL construction rather than on a
 * copy of it in a test, which would only prove the copy right.
 *
 * One footer line that is correct on a report page is WRONG on this one:
 *
 *   · the page number. "Page 01 of 1" is a lie on a sheet whose whole purpose is
 *     to be inserted at an unknown position in a hand-assembled document. Forced
 *     here rather than left to `meta`, because there is no value the caller could
 *     pass that would make numbering this sheet correct.
 *
 * "Images are not to scale" and "All units are in mm unless specified" ARE printed.
 * They were suppressed here on the reasoning that the sheet carries no image; the
 * team reported them missing on 5 Oct 2026, and the reasoning was wrong about the
 * workflow — the blank sheet is stock, and images are drawn onto it by hand before
 * anyone reads it. Both lines are the house footer's, so the sheet carries them.
 */
export function blankPageInputs(meta, template = 'orthognathic') {
  const base = geometryFor(template)
  if (!base) throw new Error(`blankPageInputs: no geometry for the "${template}" format`)
  // Numbering off through the one shared rule — see looseSheetMeta.
  const doc = newReport({ template, meta: looseSheetMeta(meta) })
  doc.pages.push(newTextPage('blank', []))
  return { doc, geometry: base }
}

/**
 * One empty sheet carrying the template's chrome and nothing else.
 *
 * The team's workflow is HYBRID while this tool is finished: some pages come
 * from the Builder, some are still composed by hand in ZBrush/Photoshop and
 * merged in with PDF24. This gives them branded stock to compose onto, so a
 * hand-built page still carries the logo, the titles, the identity bar, the
 * rules and the watermark.
 *
 * Built through the SAME chassis as the report — newReport → layoutReport →
 * buildCaseReport — rather than drawing the furniture a second time, so the
 * blank sheet cannot drift from the report's own. An empty body emits no ops
 * from textOps and empty captions collapse the caption ring, so what survives
 * is exactly the page furniture.
 *
 * Deliberately a SEPARATE download, not a page appended to the report: it needs
 * no mesh render, so it is instant and works with no case folder loaded.
 *
 * @returns {Promise<{doc: import('jspdf').jsPDF, warnings: string[]}>}
 */
export async function buildBlankPage(meta, {
  template = 'orthognathic', logo = null, logoAspect = 3,
} = {}) {
  // Two footer lines that are correct on a report page are WRONG on this one,
  // and both were caught only by looking at the sheet:
  //
  //   · the page number. "Page 01 of 1" is a lie on a sheet whose whole purpose
  //     is to be inserted at an unknown position in a hand-assembled document.
  //     Forced off here rather than left to meta, because there is no value the
  //     caller could pass that would make numbering this sheet correct.
  //   · "Images are not to scale", on a page that carries no image.
  const { doc, geometry } = blankPageInputs(meta, template)

  // No image can be requested — there are no scenes and no assets — so a
  // resolver that always returns null is the honest one, not a stub hiding work.
  return buildCaseReport(doc, { resolveImage: () => null, logo, logoAspect, geometry })
}

export { PW, PH }
